import React from "react";
import { Link } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";
import "../Footer.css";

/* ── Data ─────────────────────────────────────────────────── */
const NAV_COLS = [
  {
    title: "Product", icon: "apps", accent: "#6366f1",
    links: [
      { label: "Dashboard",   to: "/dashboard",  icon: "dashboard",          accent: "#6366f1" },
      { label: "Calculators", to: "/calculators", icon: "calculate",          accent: "#8b5cf6" },
      { label: "Pricing",     to: "/pricing",    icon: "workspace_premium",  accent: "#ec4899" },
      { label: "Blog",        to: "/blog",       icon: "article",             accent: "#06b6d4" },
    ],
  },
  {
    title: "Resources", icon: "library_books", accent: "#10b981",
    links: [
      { label: "All Calculators",  to: "/calculators", icon: "grid_view",       accent: "#10b981" },
      { label: "Best Loan Rates",  to: "/best",        icon: "trending_up",     accent: "#3b82f6" },
      { label: "Loan Comparisons", to: "/compare",     icon: "compare_arrows",  accent: "#f59e0b" },
      { label: "History",          to: "/history",     icon: "history",         accent: "#8b5cf6" },
    ],
  },
  {
    title: "Popular Categories", icon: "category", accent: "#3b82f6",
    links: [
      { label: "Home Loans",    to: "/calculators/home-loan",    icon: "house_siding",     accent: "#3b82f6" },
      { label: "Mortgages",     to: "/calculators/mortgage",     icon: "account_balance",  accent: "#6366f1" },
      { label: "Auto Loans",    to: "/calculators/auto-loan",    icon: "directions_car",   accent: "#10b981" },
      { label: "Student Loans", to: "/calculators/student-loan", icon: "school",           accent: "#f59e0b" },
    ],
  },
  {
    title: "Company", icon: "business", accent: "#8b5cf6",
    links: [
      { label: "About Us",         to: "/about",            icon: "info",        accent: "#6366f1" },
      { label: "Contact",          to: "/contact",          icon: "mail",        accent: "#10b981" },
      { label: "Editorial Policy", to: "/editorial-policy", icon: "description", accent: "#10b981" },
      { label: "Disclaimer",       to: "/disclaimer",       icon: "warning",     accent: "#f59e0b" },
      { label: "Privacy Policy",   to: "/privacy-policy",   icon: "privacy_tip", accent: "#3b82f6" },
      { label: "Cookie Policy",    to: "/cookie-policy",    icon: "cookie",      accent: "#f59e0b" },
      { label: "Terms of Service", to: "/terms-of-service", icon: "gavel",       accent: "#f59e0b" },
    ],
  },
];

const SOCIALS = [
  { icon: "public", label: "Website", href: "https://financecal.org",                         accent: "#10b981", grad: "from-emerald-500 to-teal-500",  bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.3)" },
  { icon: "mail",   label: "Email",   href: "mailto:help.financecalculator@gmail.com",        accent: "#3b82f6", grad: "from-blue-500 to-indigo-500",    bg: "rgba(59,130,246,0.12)", border: "rgba(59,130,246,0.3)" },
];

const PILLS = [
  { icon: "lock",     label: "256-bit SSL",  color: "#10b981", bg: "rgba(16,185,129,0.1)",  border: "rgba(16,185,129,0.25)"  },
  { icon: "verified", label: "GDPR Ready",   color: "#6366f1", bg: "rgba(99,102,241,0.1)",  border: "rgba(99,102,241,0.25)"  },
  { icon: "bolt",     label: "Free Forever", color: "#f59e0b", bg: "rgba(245,158,11,0.1)",  border: "rgba(245,158,11,0.25)"  },
];

const TRUST_BAR = [
  { icon: "verified_user", label: "SSL Secured",   cls: "footer-trust-icon-green"  },
  { icon: "shield",        label: "Privacy First", cls: "footer-trust-icon-indigo" },
  { icon: "bolt",          label: "No Credit Card", cls: "footer-trust-icon-amber" },
];

/* ── LinkCol ──────────────────────────────────────────────── */
const LinkCol = ({ title, icon, accent, links }: {
  title: string; icon: string; accent: string;
  links: { label: string; to: string; icon: string; accent: string }[];
}) => (
  <div className="flex flex-col">
    <h2 className="flex items-center gap-2.5 text-[11px] font-black uppercase tracking-[0.2em] mb-6 text-slate-900 dark:text-slate-300 opacity-90">
      <span
        className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm border"
        style={{ background: `${accent}12`, borderColor: `${accent}25` }}
      >
        <span
          className="material-symbols-outlined text-[15px]"
          style={{ color: accent, fontVariationSettings: "'FILL' 1" }}
        >{icon}</span>
      </span>
      {title}
    </h2>
    <ul className="space-y-3">
      {links.map((l) => (
        <li key={l.to}>
          <Link
            to={l.to}
            className="group flex items-center gap-3.5 text-[14px] font-semibold text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-white transition-all duration-300 w-fit"
          >
            <span
              className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-500 group-hover:scale-110 group-hover:rotate-[10deg] group-hover:shadow-lg group-hover:shadow-indigo-500/10 border border-transparent group-hover:border-indigo-500/20"
              style={{ background: `${l.accent}08` }}
            >
              <span
                className="material-symbols-outlined text-[14px] opacity-60 group-hover:opacity-100 transition-all duration-500"
                style={{ color: l.accent, fontVariationSettings: "'FILL' 1" }}
              >{l.icon}</span>
            </span>
            <span>{l.label}</span>
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

/* ── Footer ───────────────────────────────────────────────── */
const Footer = () => {
  const { isAuthenticated } = useAuthStore();

  const cols = NAV_COLS.map((c) =>
    c.title === "Company"
      ? { ...c, links: c.links.filter((l) => l.label !== "Profile" || isAuthenticated) }
      : c
  );

  return (
    <footer
      className="relative overflow-x-hidden pt-10"
      style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
    >
      {/* Ambient blobs - Optimized with contain: paint */}
      <div
        className="absolute top-0 left-0 w-[600px] h-[400px] rounded-full blur-3xl pointer-events-none opacity-[0.05] z-[-1]"
        style={{ background: "radial-gradient(circle,rgba(99,102,241,0.8),transparent 70%)", contain: "paint" }}
        aria-hidden="true"
      />
      <div
        className="absolute bottom-0 right-0 w-[500px] h-[400px] rounded-full blur-3xl pointer-events-none opacity-[0.04] z-[-1]"
        style={{ background: "radial-gradient(circle,rgba(139,92,246,0.8),transparent 70%)", contain: "paint" }}
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── MAIN CONTENT ── */}
        <div className="py-12 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">

            {/* Brand col */}
            <div className="lg:col-span-4 space-y-7 lg:pr-12">
              <Link to="/" className="inline-flex items-center gap-3 group/logo" aria-label="FinCalc Home">
                <div className="relative">
                  <div
                    className="absolute inset-0 rounded-2xl blur-lg opacity-35 group-hover/logo:opacity-65 transition-opacity duration-300"
                    style={{ background: "linear-gradient(135deg,#6366f1,#8b5cf6)" }}
                  />
                  <div className="relative w-10 h-10 rounded-xl overflow-hidden shadow-lg shadow-indigo-500/25">
                    <img src="/logo.webp" alt="FinCalc" className="w-full h-full object-cover" draggable={false} width="40" height="40" />
                  </div>
                </div>
                <div className="flex flex-col leading-none">
                  <span className="text-lg font-black tracking-tight text-slate-900 dark:text-white">
                    FinCalc
                  </span>
                  <span className="text-[9px] font-bold uppercase tracking-widest mt-0.5 text-indigo-500 dark:text-indigo-400">Smart Finance</span>
                </div>
              </Link>

              <p className="text-[15px] text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                AI-powered financial calculators for smarter decisions. Instant results for USA, UK, UAE, India &amp; Canada. Powering financial freedom worldwide.
              </p>

              <div className="flex gap-3">
                {SOCIALS.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    aria-label={`Follow us on ${s.label}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="footer-social-btn group relative w-11 h-11 flex items-center justify-center rounded-xl transition-all duration-500 hover:scale-110 active:scale-95"
                    style={{ 
                      background: s.bg, 
                      border: `1px solid ${s.border}`,
                      color: s.accent
                    } as any}
                  >
                    {/* Background Gradient on Hover */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${s.grad} opacity-0 group-hover:opacity-100 transition-all duration-500 rounded-xl`} />
                    
                    {/* Glossy Shimmer */}

                    {/* Icon */}
                    <span
                      className="material-symbols-outlined text-[20px] relative z-10 transition-all duration-500 group-hover:text-white group-hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]"
                      style={{ fontVariationSettings: "'FILL' 1" }}
                    >{s.icon}</span>
                    
                    {/* Outer Glow */}
                    <div 
                      className={`absolute -inset-1 rounded-2xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500 -z-10`}
                      style={{ background: s.accent }}
                    />
                  </a>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                {PILLS.map((p) => (
                  <span
                    key={p.label}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold transition-all duration-300 hover:brightness-110"
                    style={{ background: p.bg, border: `1px solid ${p.border}`, color: p.color }}
                  >
                    <span
                      className="material-symbols-outlined text-[12px]"
                      style={{ fontVariationSettings: "'FILL' 1" }}
                    >{p.icon}</span>
                    {p.label}
                  </span>
                ))}
              </div>
            </div>

            {/* Nav grid */}
            <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-12">
              {cols.map((col) => (
                <LinkCol
                  key={col.title}
                  title={col.title}
                  icon={col.icon}
                  accent={col.accent}
                  links={col.links}
                />
              ))}
            </div>
          </div>
        </div>

        {/* ── BOTTOM BAR ── */}
        <div className="py-8 border-t border-white/5">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8 text-center sm:text-left">
              <p className="text-[13px] text-slate-500 font-bold">
                © {new Date().getFullYear()} FinCalc Inc.
              </p>
              <div className="hidden sm:block w-px h-4 bg-white/10"></div>
              <div className="flex flex-wrap justify-center sm:justify-start items-center gap-6">
                {TRUST_BAR.map((t) => (
                  <span
                    key={t.label}
                    className="flex items-center gap-2 text-[12px] text-slate-500 font-medium group cursor-default"
                  >
                    <span
                      className={`material-symbols-outlined text-[16px] ${t.cls} transition-transform duration-300 group-hover:scale-125`}
                      style={{ fontVariationSettings: "'FILL' 1" }}
                    >{t.icon}</span>
                    <span className="group-hover:text-slate-400 transition-colors">{t.label}</span>
                  </span>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-center lg:justify-end gap-x-6 sm:gap-x-8 gap-y-3">
              {[
                { label: "Editorial Policy", to: "/editorial-policy" },
                { label: "Disclaimer", to: "/disclaimer" },
                { label: "Privacy Policy", to: "/privacy-policy" },
                { label: "Cookie Policy", to: "/cookie-policy" },
                { label: "Terms of Service", to: "/terms-of-service" },
              ].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="text-slate-500 hover:text-indigo-400 transition-all text-[13px] font-bold group relative"
                >
                  {l.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-indigo-500 group-hover:w-full transition-all duration-300 rounded-full shadow-[0_0_8px_rgba(99,102,241,0.5)]" />
                </Link>
              ))}
            </div>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;